import React, { useState, useEffect } from 'react';
import { 
  Dialog, DialogContent, DialogActions, TextField, Button, Box, Typography 
} from '@mui/material';
import Heading from '../../Components/Heading/heading.tsx';
import { endTripApi, getCurrentTrip } from './action.ts'; 
import Dropdown from '../../Components/DropDown/drop-down.tsx';
import { SelectChangeEvent } from '@mui/material/Select';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { renderTimeViewClock } from '@mui/x-date-pickers/timeViewRenderers';
import dayjs from 'dayjs';

interface TripDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  type: 'start' | 'end' | 'handover';
  tripId: string;
  vehicleId: string;
  vehicleList: { id: string, number_plate: string }[];
  orderPicked?: number;
  orderDelivered?: number; 
}

const TripDetailsModal: React.FC<TripDetailsModalProps> = ({ 
  isOpen, 
  onClose, 
  type, 
  tripId, 
  vehicleId, 
  vehicleList,
  orderDelivered,
  orderPicked
}) => {
  const getHeading = () => {
    switch (type) {
      case 'start': return 'Trip Details - Start';
      case 'end': return 'Trip Details - End';
      case 'handover': return 'Trip Details - Handover Acceptance';
      default: return 'Trip Details';
    }
  };

  const getTodayString = () => {
    return new Date().toISOString().split('T')[0];
  };

  const getCurrentTimeStr = () => {
    const now = new Date();
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    return `${hours}:${minutes}`;
  };

  const heading = getHeading();
  const [tripDate, setTripDate] = useState(getTodayString());
  
  const [includeTime, setIncludeTime] = useState(false);
  const [tripTime, setTripTime] = useState(getCurrentTimeStr());

  const [odometerStart, setOdometerStart] = useState('');
  const [odometerEnd, setOdometerEnd] = useState('');
  const [ordersCreated, setOrdersCreated] = useState('');
  const [ordersDelivered, setOrdersDelivered] = useState('');
  const [cratesNumber, setCratesNumber] = useState('');
  const [ordersReturned, setOrdersReturned] = useState('');
  const [selectedVehicle, setSelectedVehicle] = useState(vehicleId || '');
  const [odometerStartError, setOdometerStartError] = useState('');
  const [odometerEndError, setOdometerEndError] = useState('');
  const [ordersPickedUpError, setOrdersPickedUpError] = useState('');
  const [ordersDeliveredError, setOrdersDeliveredError] = useState('');
  const [cratesNumberError, setCratesNumberError] = useState('');
  const [ordersReturnedError, setOrdersReturnedError] = useState('');
  const [isCheckingTrip, setIsCheckingTrip] = useState(false);
  const [currentTripId, setCurrentTripId] = useState(tripId || '');
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isVehicleFixed = isMobile && Boolean(vehicleId);

  useEffect(() => {
    if (type === 'handover' && ordersCreated && ordersDelivered) {
      const picked = parseInt(ordersCreated) || 0;
      const delivered = parseInt(ordersDelivered) || 0;
      const returned = picked - delivered;
      setOrdersReturned(returned >= 0 ? returned.toString() : '0');
    }
  }, [ordersCreated, ordersDelivered, type]);
  
  useEffect(() => {
    if (isOpen && (type === 'end' || type === 'handover')) {
      setOrdersDelivered(orderDelivered?.toString() || '');
    }
  }, [isOpen, type, orderDelivered]);
  
  useEffect(() => {
    if (isOpen && selectedVehicle) {
      checkCurrentTrip(selectedVehicle);
    }
  }, [isOpen, type, selectedVehicle]);
  
  useEffect(() => {
    if (isOpen) {
      setTripDate(getTodayString());
      setIncludeTime(false);
      setTripTime(getCurrentTimeStr());

      if (type === 'handover') {
        setOdometerStart('');
        setOdometerEnd('');
        setOrdersCreated('');
        setOrdersDelivered('');
        setCratesNumber('');
        setOrdersReturned('');
        setOdometerStartError('');
        setOdometerEndError('');
        setOrdersPickedUpError('');
        setOrdersDeliveredError('');
        setCratesNumberError('');
        setOrdersReturnedError('');
      } else if (type === 'start') {
        setOdometerStart('');
        setOrdersCreated('');
        setSelectedVehicle(vehicleId || '');
      } else if (type === 'end') {
        setOdometerEnd('');
        setOrdersDelivered('');
        setSelectedVehicle(vehicleId);
      }
    }
  }, [isOpen, type, vehicleId]);

  const checkCurrentTrip = (vehicleId: string) => {
    setIsCheckingTrip(true);
    getCurrentTrip(
      (response: any) => {
        setIsCheckingTrip(false);
        if (response && response.message.id) {
          setCurrentTripId(response.message.id);
          setOrdersCreated(response.message.orders_picked);
        } else {
          setCurrentTripId('');
        }
      },
      (error: any) => {
        setIsCheckingTrip(false);
        if (error.status === 404) {
          setCurrentTripId('');
        } else {
          toast.error('Error checking current trip');
        }
      },
      '',
      vehicleId
    );
  };

  const handleConfirmStartTrip = () => {
    setShowConfirmationModal(false);
    proceedWithStartTrip(currentTripId);
  };

  const handleCancelStartTrip = () => {
    setShowConfirmationModal(false);
    setCurrentTripId('');
    proceedWithStartTrip('');
  };

  const getFinalDateTime = (date: string) => {
    if (includeTime && tripTime) {
      return `${date} ${tripTime}:00`;
    }
    const now = new Date();
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    const seconds = String(now.getSeconds()).padStart(2, '0');
    return `${date} ${hours}:${minutes}:${seconds}`;
  };

  const proceedWithStartTrip = (tripId: any) => {
    if (!selectedVehicle) {
      toast.error("Please choose a vehicle");
      return;
    }
    if (odometerStartError || ordersPickedUpError) {
      toast.error("Please fix the errors");
      return;
    }
    
    const tripDateTime = getFinalDateTime(tripDate);
    setIsSubmitting(true);
    endTripApi(
      12.834932,
      77.606037,
      tripId,
      selectedVehicle || vehicleId,
      (response: any) => {
        setIsSubmitting(false);
        if (response.data.status === 201 || response.data.status === 200) {
          toast.success(response.data.message.message);
          setSelectedVehicle('');
          onClose();
        } else if (response.data.status === 400) {
          toast.error(response.data.message?.message || response.data.message);
        }
      },
      (errMessage: any) => {
        setIsSubmitting(false);
        if (errMessage.status === 404) {
          toast.error(errMessage.message.errors);
        }
      },
      'start',
      tripDateTime,
      '',
      odometerStart,
      odometerEnd,
      ordersCreated,
      ordersDelivered,
      '',
      ''
    );
  };

  const handleSave = () => {
    if (type === 'start') {
      if (odometerStartError || ordersPickedUpError) {
        toast.error("Please fix the errors");
        return;
      }
      if (currentTripId) {
        setShowConfirmationModal(true);
      } else {
        proceedWithStartTrip('');
      }
    } else if (type === 'end') {
      if (odometerEndError || ordersDeliveredError) {
        toast.error("Please fix the errors");
        return;
      }
      const tripDateTime = getFinalDateTime(tripDate);
      const tripIdToUse = currentTripId || tripId;
      setIsSubmitting(true);
      endTripApi(
        12.834932,
        77.606037,
        tripIdToUse,
        selectedVehicle || vehicleId,
        (response: any) => {
          setIsSubmitting(false);
          if (response.data.status === 201 || response.data.status === 200) {
            toast.success(response.data.message.message);
            setSelectedVehicle('');
            onClose();
          } else if (response.data.status === 400) {
            toast.error(response.data.message?.message || response.data.message);
          }
        },
        (errMessage: any) => {
          setIsSubmitting(false);
          if (errMessage.status === 404) {
            toast.error(errMessage.message.errors);
          }
        },
        'end',
        tripDateTime,
        '',
        odometerStart,
        odometerEnd,
        ordersCreated,
        ordersDelivered,
        '',
        ''
      );
    } else if (type === 'handover') {
      if (odometerEndError || ordersDeliveredError || cratesNumberError) {
        toast.error("Please fix the errors");
        return;
      }

      const picked = parseInt(ordersCreated) || 0;
      const delivered = parseInt(ordersDelivered) || 0;
      const calculatedReturned = picked - delivered;
      
      if (calculatedReturned < 0) {
        toast.error("Orders delivered cannot exceed orders picked");
        return;
      }

      const startReading = Number(odometerStart);
      const endReading = Number(odometerEnd);
      if (endReading < startReading) {
        toast.error("Odometer end reading must be greater than or equal to start reading");
        return;
      }

      setIsSubmitting(true);
      const tripDateTime = getFinalDateTime(tripDate);

      endTripApi(
        12.834932,
        77.606037,
        tripId,
        vehicleId,
        (response: any) => {
          setIsSubmitting(false);
          if (response.data.status === 201 || response.data.status === 200) {
            toast.success(response.data.message.message || 'Handover accepted successfully');
            onClose();
            setSelectedVehicle('');
          } else if (response.data.status === 400) {
            toast.error(response.data.message?.message || response.data.message);
          }
        },
        (errMessage: any) => {
          setIsSubmitting(false);
          if (errMessage.status === 404) {
            toast.error(errMessage.message?.errors || 'Handover failed');
          } else {
            toast.error(errMessage.message || 'Failed to process handover');
          }
        },
        'end',
        tripDateTime,
        '', 
        odometerStart,
        odometerEnd,
        ordersCreated,
        ordersDelivered,
        ordersReturned,
        cratesNumber,
      );
    }
  };

  const handleVehicleChange = (event: SelectChangeEvent<string>) => {
    const newVehicleId = event.target.value;
    setSelectedVehicle(newVehicleId);
    setCurrentTripId('');
    setShowConfirmationModal(false);
  };

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTripDate(e.target.value);
  };

  const handleOdometerStartChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const decimalRegex = /^\d*\.?\d*$/;
    
    if (!decimalRegex.test(value)) {
      setOdometerStartError('Invalid Input');
    } else {
      setOdometerStartError('');
    }
    setOdometerStart(value);
  };

  const handleOdometerEndChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const decimalRegex = /^\d*\.?\d*$/;
    
    if (!decimalRegex.test(value)) {
      setOdometerEndError('Invalid Input');
    } else {
      setOdometerEndError('');
    }
    setOdometerEnd(value);
  };

  const handleOrdersPickedChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (/[^0-9]/.test(value)) {
      setOrdersPickedUpError('Invalid Input');
    } else {
      setOrdersPickedUpError('');
    }
    setOrdersCreated(value);
  };

  const handleOrdersDeliveredChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (/[^0-9]/.test(value)) {
      setOrdersDeliveredError('Invalid Input');
    } else {
      setOrdersDeliveredError('');
    }
    setOrdersDelivered(value);
  };

  const handleCratesNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (/[^0-9]/.test(value)) {
      setCratesNumberError('Invalid Input');
    } else {
      setCratesNumberError('');
    } 
    setCratesNumber(value);
  };

  const handleOrdersReturnedChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (/[^0-9]/.test(value)) {
      setOrdersReturnedError('Invalid Input');
    } else {
      setOrdersReturnedError('');
    } 
    setOrdersReturned(value);
  };

  const handleCancel = () => {
    if (type === 'start') {
      setSelectedVehicle('');
      setOdometerStartError('');
      setOrdersPickedUpError('');
      setOdometerStart('');
      setOrdersCreated('');
      setCurrentTripId('');
      setTripDate(getTodayString());
      setShowConfirmationModal(false);
    } else if (type === 'handover') {
      setOdometerStart('');
      setOdometerEnd('');
      setOrdersCreated('');
      setOrdersDelivered('');
      setCratesNumber('');
      setOrdersReturned('');
      setOdometerStartError('');
      setOdometerEndError('');
      setOrdersPickedUpError('');
      setOrdersDeliveredError('');
      setCratesNumberError('');
      setOrdersReturnedError('');
    } else {
      setOdometerEnd('');
      setOrdersDelivered('');
      setOrdersDeliveredError('');
      setOdometerEndError('');
    }
    setIncludeTime(false);
    setTripTime(getCurrentTimeStr());
    onClose();
  };

  const getButtonText = () => {
    switch (type) {
      case 'start': return 'Start Trip';
      case 'end': return 'End Trip';
      case 'handover': return 'Accept Handover';
      default: return 'Save';
    }
  };

  const getButtonColor = () => {
    switch (type) {
      case 'start': return 'green';
      case 'end': return 'red';
      case 'handover': return 'orange';
      default: return 'primary';
    }
  };

  return (
    <>
      <Dialog open={isOpen} onClose={onClose} maxWidth="sm">
        <Box sx={{ marginLeft: { sm: '-180px' } }}>
          <Heading title={heading} closeIcon='true' onClose={onClose} />
        </Box>
        <DialogContent
          sx={{
            minWidth: '200px',
            minHeight: type === 'handover' ? '400px' : '300px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            overflowY: 'auto'
          }}
        >
          {type === 'start' && (
            <>
              <Dropdown
                options={vehicleList.map((vehicle) => ({
                  id: vehicle.id,
                  label: vehicle.number_plate,
                }))}
                selectedValue={selectedVehicle || ''}
                handleChange={handleVehicleChange}
                placeholder="Choose a vehicle"
                label="Choose a vehicle"
                width="250px"
                height="50px"
                searchable={true}
                name="number_plate"
                disabled={isVehicleFixed}
              />
              {isCheckingTrip && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Checking for active trips...
                </Typography>
              )}
            </>
          )}

          <TextField
            label="Trip Date"
            type="date"
            fullWidth
            margin="normal"
            variant="outlined"
            value={tripDate}
            onChange={handleDateChange}
            sx={{ width: '250px' }}
            InputLabelProps={{
              shrink: true,
            }}
          />

          <Box sx={{ width: '250px', display: 'flex', justifyContent: 'flex-start', mt: -1, mb: 1 }}>
            <Button 
              size="small" 
              onClick={() => setIncludeTime(!includeTime)}
              sx={{ textTransform: 'none', fontSize: '0.8rem', padding: 0 , color:'black'}}
              disableRipple
            >
              {includeTime ? '- Remove Custom Time' : '+ Add Custom Time'}
            </Button>
          </Box>

          {/* --- Updated TimePicker Component with Analog Clock View --- */}
          {includeTime && (
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <TimePicker
                label="Trip Time"
                value={tripTime ? dayjs(`2024-01-01T${tripTime}`) : null}
                onChange={(newValue) => {
                  if (newValue) {
                    setTripTime(newValue.format('HH:mm'));
                  }
                }}
                viewRenderers={{
                  hours: renderTimeViewClock,
                  minutes: renderTimeViewClock,
                  seconds: renderTimeViewClock,
                }}
                slotProps={{
                  desktopPaper: {
                    sx: { borderRadius: '16px', padding: '8px' }
                  }
                }}
                sx={{ width: '250px', mt: 1, mb: 1 }}
              />
            </LocalizationProvider>
          )}

          {(type === 'start') && (
            <>
              <TextField
                label="Odometer Start"
                fullWidth
                margin="normal"
                variant="outlined"
                type='text'
                value={odometerStart}
                onChange={handleOdometerStartChange}
                sx={{ width: '250px' }}
                placeholder="Enter start reading"
              />
              {odometerStartError && (
                <Typography color="error" sx={{ width: '250px', textAlign: 'left' }}>
                  {odometerStartError}
                </Typography>
              )}

              <TextField
                label="Orders Picked"
                fullWidth
                margin="normal"
                variant="outlined"
                type="text"
                value={ordersCreated}
                onChange={handleOrdersPickedChange}
                sx={{ width: '250px' }}
                placeholder="Enter orders picked"
              />
              {ordersPickedUpError && (
                <Typography color="error" sx={{ width: '300px', textAlign: 'left' }}>
                  {ordersPickedUpError}
                </Typography>
              )}
            </>
          )}

          {(type === 'end' || type === 'handover') && (
            <>
              <TextField
                label="Odometer End"
                fullWidth
                margin="normal"
                variant="outlined"
                type='text'
                value={odometerEnd}
                onChange={handleOdometerEndChange}
                sx={{ width: '250px' }}
                placeholder="Enter end reading"
              />
              {odometerEndError && (
                <Typography color="error" sx={{ width: '250px', textAlign: 'left' }}>
                  {odometerEndError}
                </Typography>
              )}

              <TextField
                label="Orders Delivered"
                fullWidth
                margin="normal"
                variant="outlined"
                type="text"
                value={ordersDelivered }
                onChange={handleOrdersDeliveredChange}
                sx={{ width: '250px' }}
                placeholder="Enter orders delivered"
              />
              {ordersDeliveredError && (
                <Typography color="error" sx={{ width: '300px', textAlign: 'left' }}>
                  {ordersDeliveredError}
                </Typography>
              )}
            </>
          )}

          {type === 'handover' && (
            <>
              <TextField
                label="Orders Picked"
                fullWidth
                margin="normal"
                variant="outlined"
                type="text"
                value={ordersCreated || orderPicked}
                onChange={handleOrdersPickedChange}
                sx={{ width: '250px' }}
                placeholder="Enter orders picked"
              />
              {ordersPickedUpError && (
                <Typography color="error" sx={{ width: '300px', textAlign: 'left' }}>
                  {ordersPickedUpError}
                </Typography>
              )}

              <TextField
                label="Crates Number"
                fullWidth
                margin="normal"
                variant="outlined"
                type="text"
                value={cratesNumber}
                onChange={handleCratesNumberChange}
                sx={{ width: '250px' }}
                placeholder="Enter number of crates"
              />
              {cratesNumberError && (
                <Typography color="error" sx={{ width: '250px', textAlign: 'left' }}>
                  {cratesNumberError}
                </Typography>
              )}

              <TextField
                label="Number of Orders Returned"
                fullWidth
                margin="normal"
                variant="outlined"
                type="text"
                value={ordersReturned}
                onChange={handleOrdersReturnedChange}
                sx={{ width: '250px' }}
                placeholder="Enter number of orders returned"
              />
              {ordersReturnedError && (
                <Typography color="error" sx={{ width: '300px', textAlign: 'left' }}>
                  {ordersReturnedError}
                </Typography>
              )}
            </>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 2 }}>
            <Button 
              onClick={handleCancel} 
              variant="outlined"  
              className="custom-cancel-button"
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={isSubmitting}
              sx={{
                backgroundColor: getButtonColor(),
                color: 'white',
                borderRadius:'20px',
                '&:hover': {
                  backgroundColor: getButtonColor(),
                  filter: 'brightness(0.9)',
                },
                '&:disabled': {
                  backgroundColor: 'grey',
                }
              }}
            >
              {isSubmitting ? 'Processing...' : getButtonText()}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
      <Dialog open={showConfirmationModal} onClose={() => setShowConfirmationModal(false)}>
        <Box>
          <Box sx={{ marginLeft: { sm: '-180px' } }}>
            <Heading title="Confirm Trip Start" closeIcon='true' onClose={() => setShowConfirmationModal(false)} />
          </Box>
          <DialogContent>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Do you want to start the assigned trip or begin a new trip? 
            </Typography>
          </DialogContent>
          <DialogActions sx={{justifyContent:'center', display:"flex"}}>
            <Button onClick={handleCancelStartTrip} variant="outlined" className="custom-cancel-button">
              Start New Trip
            </Button>
            <Button 
              variant="contained"
              sx={{backgroundColor:'green'}}
              onClick={handleConfirmStartTrip}
            >
              Start Assigned Trip
            </Button>
          </DialogActions>
        </Box>
      </Dialog>
    </>
  );
};

export default TripDetailsModal;